import React, { useState, useMemo, useEffect, useContext } from "react";
import CONFIG from "config/config";
import cn from "classnames";
import { Helmet } from "react-helmet";
import { toFixed3 } from "utils/formators";
import { withStyles } from "@material-ui/core/styles";
import { capitalize } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useHistory } from "react-router";
import { authorization } from "module/auth";
import { SnackBarContext } from "components/snack-bar";
import { DialogModalContext } from "components/dialog-modal";
import Header from "components/header";
import { SuccessIcon } from "components/icons/components/SuccessIcon";
import { CAMPAIGN_ENUM_TYPES, USER_CAMPAIGN_ENUM_TYPES, DocumentNames, DMDocumentLabels } from "shared/constants";
import { FlyerStatus } from "pages/flyer/flyerTypePage/useGetDataForPage";
import { deleteRedirectedCampaign } from "utils/getRedirectedCampaigns";
import CheckoutSidebar from "campaign-builder/checkout/sidebar";
import { CampaignSubmissionModal } from "components/CampaignSubmissionModal";
import { apiClient } from "module/api";
import { Printer } from "lucide-react";
import { isBelowMinimumCampaignValue } from "../../../../../utils/minimumCampaignValue";
import { getMinimumCampaignModalConfig } from "../../../../../utils/minimumCampaignModal";
import { DM_MIN_SELECTABLE_DATE } from "../../../../../constants";
import { CouponCode } from "../../../../../components/CouponCode";
import { AlertInvalidMissions } from "../../../../../components/audience-form/components/campaign-location/h2h-precise-locations/components/AlertInvalidMissions/AlertInvalidMissions.tsx";
import { convertNumber, convertNumberRounded, renderStatus } from "utils/campaignQuote";
import { formatDate, customDateFormat } from "../../../../../utils/date";
import {
  EDDM_NAVIGATION,
  NAVIGATION,
  READABLE_CAMPAIGN_INTERNAL_STATUSES,
  CAMPAIGN_INTERNAL_STATUSES,
} from "../../../../constants";
import { useStore } from "../../../../../store";
import { style } from "./style";
import { UploadFlyerSection } from "./components";
import { SubmitCampaignFooter } from "./components/SubmitCampaignFooter";
import { useBillingGateSurface } from "campaign-builder/checkout/billing-profile/useBillingGateSurface";
import { Card } from "components/ui/card";
import "../../../../../campaign-builder/ui/theme.css";

const hideOnNull = (obj, block) => {
  if (obj?.rate) {
    return block;
  }
  return null;
};

const displayOnCondition = (condition, block) => {
  if (condition) {
    return block;
  }
  return null;
};

const QuoteView = ({
  classes,
  isPaymentSkipped,
  stripeCouponCode,
  couponErrorMessage,
  setCouponErrorMessage,
  data,
  onSubmit,
  dates,
  refs,
  handlePrint,
  toAudience,
  setIsModalOpen,
  loading,
  handleViewDesignClick,
  isSubmitConfirmationModalOpen,
  setIsSubmitConfirmationModalOpen,
  onApplyCouponCode,
  detailedCost,
  handleDatesChange,
  pageState,
  campaignId,
  flyerInfo,
  checkoutComponent,
  reviewOfferComponent,
  reviewDiscount,
  showCheckout,
  onCloseCheckout,
}) => {
  const posthog = usePostHog();
  const [couponCodeOpen, setCouponCodeOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const runSnackBar = useContext(SnackBarContext);
  const runDialogModal = useContext(DialogModalContext);
  const history = useHistory();

  useEffect(() => {
    if (stripeCouponCode) {
      setCouponCodeOpen(true);
    }
  }, [stripeCouponCode]);

  const {
    submitter,
    submittedBy,
    campaignName,
    campaignDuration,
    flyerType,
    flyerWeight,
    currencySign,
    clientName,
    updatedAt,
    internalStatus = CAMPAIGN_INTERNAL_STATUSES.DRAFT,
    isSubmitted,
    quote,
    country,
    channel,
    amountOff,
    percentOff,
    cityName,
    isDM,
    flyersAmount,
    costPerFlyer,
    couponType: couponTypeDB,
    staticCouponCode: staticCouponCodeDB,
    dynamicCouponCode: dynamicCouponCodeDB,
  } = data;
  const { distribution, permit, printing, travel, outfit, subtotal, tax, totalCost } = detailedCost;
  // HUB-440: the review discount is not part of EDDM's FE cost math, so the
  // quote subtracts it here for display. The authoritative figure is still the
  // server breakdown at checkout (/billing), which applies the same discount.
  const isReviewDiscountApplied = !!reviewDiscount?.isApplied;
  const displaySubtotal = isReviewDiscountApplied ? Math.max(0, subtotal - reviewDiscount.amountOff) : subtotal;
  const displayTotalCost = isReviewDiscountApplied ? Math.max(0, totalCost - reviewDiscount.amountOff) : totalCost;
  const { startDate } = dates;
  const { firstPageRef, secondPageRef } = refs;
  const user = authorization.getUser();
  const isClient = authorization.isClient();
  const status = useMemo(() => READABLE_CAMPAIGN_INTERNAL_STATUSES[internalStatus], [internalStatus]);
  const isShowWrongDatesModal = startDate.error && !isSubmitted;
  const {
    user: { isVerified },
    client: { id: clientId },
    city: storeCity,
  } = useStore();
  const billingGate = useBillingGateSurface(clientId);

  const isUS = country.code === "US";
  const isUK = country.code === "UK";
  const defaultCampaignLimits = isDM ? DM_MIN_SELECTABLE_DATE : country.countryDefaultSettings.defaultCampaignLimits;
  const isD2D = channel === CAMPAIGN_ENUM_TYPES.LETTERBOX;
  const showCoupon = (!isSubmitted || !!stripeCouponCode) && !isPaymentSkipped;
  const notExistingDesignFile =
    pageState === FlyerStatus.flyerAbsent || (!flyerInfo?.s3Link && flyerInfo?.activeDesignId);

  const handleClickSubmitAndPay = async () => {
    if (!isDM && isBelowMinimumCampaignValue(country?.name, storeCity?.name, subtotal)) {
      const modalConfig = getMinimumCampaignModalConfig({
        countryName: country?.name,
        cityName: storeCity?.name,
        clientId,
        history,
        onError: (msg) => runSnackBar({ type: "error", vertical: "bottom", msg }),
      });
      runDialogModal(modalConfig);
      return;
    }
    if (!flyerInfo.s3Link) return;
    if (isVerified === false) {
      runSnackBar({
        type: "error",
        vertical: "bottom",
        msg: "For security, you must validate your email to submit the campaign.",
      });
      return false;
    }
    if (posthog) {
      posthog.capture("Clicked Campaign Submit Button", {
        event_category: "Quote",
      });
    }
    // The terms + tracking-code acknowledgement is now the inline note under the
    // button (TermsAgreementNote), so submission is no longer gated on a modal.
    if (isPaymentSkipped) {
      deleteRedirectedCampaign(campaignId);
    }
    await onSubmit();
    // `isModalOpen` no longer opens anything — the terms modal is gone — but the
    // parent still latches the `ss_submit_started` gtag event off it, so keep
    // flipping it here or that event silently stops firing.
    setIsModalOpen(true);
  };

  const onScheduleSuccess = async (reviewMeetingDate) => {
    const formattedDate = new Date(reviewMeetingDate).toISOString();
    await apiClient.updateCampaignReviewMeetingDate(campaignId, formattedDate);

    if (posthog) {
      posthog.capture("Booked Meeting", {
        campaignId: campaignId,
        companyName: clientName,
        channel: channel === CAMPAIGN_ENUM_TYPES.DIRECTMAIL ? "EDDM" : channel,
        userId: user?.id,
        meetingType: "campaign_review",
        countryName: country?.name,
        countryCode: country?.code,
        meetingDate: formattedDate,
        amount: totalCost,
      });
    }
  };

  const handleOpenCouponCode = () => {
    setCouponCodeOpen(true);
  };

  const taxNotCalculated = isUS && tax == 0 && internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT;

  const getCouponCode = () => {
    if (!showCoupon) {
      return undefined;
    }

    return (
      <div className="mob-coupon-code-wrapper mt-3 flex justify-end">
        {couponCodeOpen ? (
          <CouponCode
            stripeCouponCode={stripeCouponCode}
            errorMessage={couponErrorMessage}
            setCouponErrorMessage={setCouponErrorMessage}
            amountOff={amountOff}
            percentOff={percentOff}
            isDraft={!isSubmitted}
            currencySign={currencySign}
            isUSClient={isUS}
            onApply={onApplyCouponCode}
          />
        ) : (
          <span
            className="mob-coupon-triger cursor-pointer pr-5 text-sm font-bold leading-[18px] text-[#4f5b94] underline dark:text-muted-foreground"
            onClick={handleOpenCouponCode}
            cypress_id={"couponCodeLinkQ"}
          >
            I have a coupon code
          </span>
        )}
      </div>
    );
  };

  const handleOpenBookingModal = () => {
    setIsBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
  };

  const titleCls = "mb-3 flex items-center text-[28px] font-bold leading-[34px] text-[#222336] dark:text-foreground";
  const rowCls = "mb-1 flex [&>div]:flex-1";
  const textCls = "flex items-center break-words text-base font-normal leading-6 text-foreground";
  const linkCls = "cursor-pointer text-sm font-bold leading-[18px] text-primary underline";
  const flex1 = "flex-1";
  const flex15 = "flex-[1.5]";
  const boldCls = "font-bold";
  const centerCls = "items-center text-center";
  const tableRowCls = "flex border-b border-solid border-border [&>div]:my-2.5";
  const lastRowsCls = "flex-[3.5] [&>span]:ml-[241px]";

  return (
    <div className="cb-v2-root flex min-h-0 min-w-0 flex-1 overflow-auto bg-page max-md:overflow-visible [&::-webkit-scrollbar]:w-0">
      <Helmet>
        <title>Submit</title>
      </Helmet>
      <CampaignSubmissionModal
        isOpen={isSubmitConfirmationModalOpen}
        onConfirmation={() => {
          window.location.replace(`${CONFIG.PLATFORM_BASE_URL}/dashboard/home`);
        }}
        title="Campaign submitted!"
        body={"We'll review and confirm your campaign soon."}
        icon={SuccessIcon}
        ctaText="Back to home"
      />

      <CheckoutSidebar show={showCheckout} onClose={() => onCloseCheckout()}>
        {showCheckout && checkoutComponent()}
      </CheckoutSidebar>
      {billingGate.drawer}
      <div className="flex w-full min-w-full flex-col items-center px-8">
        {(() => {
          const banner = billingGate.renderBanner("eddm-quote");
          return banner && <div className="mt-4 w-full max-w-[1200px]">{banner}</div>;
        })()}
        <div className="mob-pages-wrapper mt-4 flex w-full max-w-[1200px] flex-wrap justify-center gap-4">
          <Card className="mob-page flex min-h-[678px] w-[592px] max-w-full cursor-default flex-col justify-between gap-0 p-[25px]">
            <div>
              <div className={titleCls} cypress_id={"quoteSpecificsTableTitle"}>
                Campaign Specifics
              </div>
              <div className={rowCls}>
                <div className={cn(textCls, "mob-text")} cypress_id={"campaignNameTitleQS"}>
                  Campaign name
                </div>
                <div className={cn(textCls, "mob-text")} cypress_id={"campaignNameValueQS"}>
                  {campaignName}
                </div>
              </div>

              {displayOnCondition(
                !isDM,
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"cityNameTitleQS"}>
                    City
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"cityNameValueQS"}>
                    {cityName}
                  </div>
                </div>
              )}

              <div className={rowCls}>
                <div className={textCls} cypress_id={"channelTitleQS"}>
                  Channel
                </div>
                <div className={cn(textCls, "mob-text")} cypress_id={"channelValueQS"}>
                  {USER_CAMPAIGN_ENUM_TYPES[channel]}
                </div>
              </div>
              {isDM ? (
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"areasOrDistributorsTitle"}>
                    Recipients
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"areasDistributorsValueAmountQS"}>
                    {flyersAmount}
                  </div>
                </div>
              ) : (
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"areasOrDistributorsTitle"}>
                    {isD2D ? "Areas " : "Distributors "}
                  </div>
                  <div className={textCls} cypress_id={"areasDistributorsValueAmountQS"}>
                    {distribution?.quantity}
                  </div>
                </div>
              )}

              {displayOnCondition(
                isDM && couponTypeDB && (staticCouponCodeDB || dynamicCouponCodeDB?.name),
                <>
                  <div className={rowCls}>
                    <div className={cn(textCls, "mob-text")} cypress_id={"channelTitleQS"}>
                      Coupon type
                    </div>
                    <div className={textCls} cypress_id={"channelValueQS"}>
                      {capitalize(couponTypeDB)}
                    </div>
                  </div>
                  <div className={rowCls}>
                    <div className={cn(textCls, "mob-text")} cypress_id={"channelTitleQS"}>
                      Coupon name
                    </div>
                    <div className={textCls} cypress_id={"channelValueQS"}>
                      {couponTypeDB === "static"
                        ? staticCouponCodeDB
                        : couponTypeDB === "dynamic"
                        ? dynamicCouponCodeDB.name
                        : "-"}
                    </div>
                  </div>
                </>
              )}

              <div className="mb-2 flex">
                <div className="flex flex-1" />
                <div className="flex flex-1">
                  <div className="flex-1">
                    {internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ? (
                      <span onClick={toAudience} className={linkCls} cypress_id={"editAudienceLinkQS"}>
                        {`Edit ${isDM ? "Segments" : "Audience"}`}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex-1 display-none" />
                </div>
              </div>

              {displayOnCondition(
                !isDM,
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyersTitleQS"}>
                    Flyers
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyersAmountValueQS"}>
                    {printing?.quantity}
                  </div>
                </div>
              )}

              {displayOnCondition(
                !isDM,
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"campaignDurationTitleQS"}>
                    Campaign duration
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"campaignDurationValueQS"}>
                    {campaignDuration} week{campaignDuration !== 1 ? "s" : ""}
                  </div>
                </div>
              )}

              <div className={rowCls}>
                <div className={cn(textCls, "mob-text")} cypress_id={"campaignLaunchDateTitleQS"}>
                  Campaign launch date
                </div>
                <div
                  className={cn(textCls, "mob-text")}
                  cypress_id={"campaignLaunchDateValueQS"}
                  data-copilot-readout="launchDate"
                >
                  {customDateFormat(startDate.value, isUS)}
                </div>
              </div>

              {isShowWrongDatesModal ? (
                <AlertInvalidMissions
                  defaultCampaignLimits={defaultCampaignLimits}
                  wrongDates
                  handleDatesChange={handleDatesChange}
                  modalType={isDM}
                />
              ) : null}
              <div className={rowCls}>
                <div className={cn(textCls, "mob-text")} cypress_id={"flyerTypeTitleQS"}>
                  {isDM ? "Mail piece format" : "Flyer type"}
                </div>
                <div className={textCls} cypress_id={"flyerTypeValueQS"}>
                  {isDM ? DMDocumentLabels[flyerType] : DocumentNames[flyerType]}
                  {notExistingDesignFile && !isDM && DocumentNames[flyerType] === DocumentNames.A6 ? (
                    <span className="ml-1 text-base text-muted-foreground">{"(default)"}</span>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              {displayOnCondition(
                !isDM,
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerWeightTitleQS"}>
                    Flyer weight
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerWeightValueQS"}>
                    {`${flyerWeight} GSM`}
                  </div>
                </div>
              )}

              {notExistingDesignFile ? (
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerDesignTitleQS"}>
                    Artwork name
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerDesignValueQS"}>
                    <span className="text-base font-normal leading-6 text-muted-foreground">No file uploaded</span>
                  </div>
                </div>
              ) : (
                <div className={rowCls}>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerDesignTitleQS"}>
                    Flyer design
                  </div>
                  <div className={cn(textCls, "mob-text")} cypress_id={"flyerDesignValueQS"}>
                    <span onClick={handleViewDesignClick} className={linkCls} cypress_id={"viewDesignLinkQS"}>
                      View design
                    </span>
                  </div>
                </div>
              )}
              {notExistingDesignFile && <UploadFlyerSection isDM={isDM} />}
              <div className="my-6 h-px w-full bg-border" />
              <div className="flex w-full items-start justify-between">
                <div className={textCls} cypress_id={"submittedByTitleQS"}>
                  {isSubmitted ? "Submitted by:" : "Submitter:"}
                  <span className="ml-1" cypress_id={"submitterValueQS"}>
                    {isSubmitted ? submittedBy : submitter}
                  </span>
                </div>
                <div className="mb-12 mr-4 flex justify-end display-none">
                  <div onClick={() => handlePrint()} className="flex cursor-pointer items-center">
                    <Printer className="h-5 w-5 text-[#222336] dark:text-foreground" />
                    <span
                      className="ml-2 flex items-center text-base font-bold leading-5 text-[#222336] underline dark:text-foreground"
                      cypress_id={"printIconQS"}
                    >
                      Print
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="second-child mob-page flex min-h-[678px] w-[592px] max-w-full cursor-default flex-col justify-between gap-0 p-[25px]">
            <div>
              <div className="flex">
                <div className={titleCls} cypress_id={"quoteTableTitle"}>
                  Quote
                </div>
                <div className={cn(textCls, "mb-1 ml-4 leading-6")} cypress_id={"quoteValueQ"}>
                  {quote}
                </div>
              </div>
              <div className="mob-row mb-3 flex">Please review details provided:</div>
              <div className="mb-1 flex items-center text-center">
                <div className="mr-10" cypress_id={"campaignNameValueQ"}>
                  {campaignName}
                </div>
                <div cypress_id={"campaignStatusValueQ"}>{renderStatus(status, data.reviewMeetingDate)}</div>
              </div>
              <div className={rowCls}>
                <div cypress_id={"companyNameTitleQ"}>Company name</div>
                <div cypress_id={"companyNameValueQ"}>{clientName}</div>
                <div />
              </div>
              <div className={rowCls}>
                <div cypress_id={"dateTitleQ"}>Date</div>
                <div cypress_id={"dateValueQ"}>
                  {status === READABLE_CAMPAIGN_INTERNAL_STATUSES.DRAFT
                    ? formatDate(new Date(), "MMM d, yyyy")
                    : formatDate(+updatedAt, "MMM d, yyyy")}
                </div>
                <div />
              </div>
              <div className="mt-[30px] flex flex-col">
                <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                  <div className={cn(flex15, boldCls)} cypress_id={"descriptionColumnTitleQ"}>
                    Description
                  </div>
                  <div className={cn(flex1, boldCls, centerCls)} cypress_id={"quanityColumnTitleQ"}>
                    Quantity
                  </div>
                  <div className={cn(flex1, boldCls, centerCls)} cypress_id={"unitRateColumnTitleQ"}>
                    Unit rate
                  </div>
                  <div className={cn(flex1, boldCls, centerCls)} cypress_id={"totalColumnTitleQ"}>
                    Total
                  </div>
                </div>
                {displayOnCondition(
                  isDM,
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"distributionCostRowTitleQ"}>
                      Mailings
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionQuantityQ"}>
                      {flyersAmount}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionUnitRateQ"}>
                      {convertNumber({
                        number: costPerFlyer,
                        currency: currencySign,
                        fixed: 4,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionTotalQ"}>
                      {convertNumber({
                        number: subtotal,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  distribution,
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"distributionCostRowTitleQ"}>
                      Distribution Cost
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionQuantityQ"}>
                      {distribution?.quantity}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionUnitRateQ"}>
                      {convertNumber({
                        number: distribution?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"distributionTotalQ"}>
                      {convertNumber({
                        number: distribution?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  printing,
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"printingCostRowTitleQ"}>
                      Printing Cost
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"printingCostQuantityQ"}>
                      {printing?.quantity}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"printingCostUnitRateQ"}>
                      {currencySign}
                      {toFixed3(printing?.rate || 0)}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"printingCostTotalQ"}>
                      {convertNumber({
                        number: printing?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  travel,
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"travelCostRowTitleQ"}>
                      Travel Cost
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"travelCostQuantityQ"}>
                      {travel?.quantity}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"travelCostUnitRateQ"}>
                      {convertNumber({
                        number: travel?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"travelCostTotalQ"}>
                      {convertNumber({
                        number: travel?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  permit,
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"permitCostRowTitleQ"}>
                      Permit Cost
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"permitCostQuantityQ"}>
                      {permit?.quantity}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"permitCostUnitRateQ"}>
                      {convertNumber({
                        number: permit?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"permitCostTotalQ"}>
                      {convertNumber({
                        number: permit?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {outfit?.total ? (
                  <div className={cn(tableRowCls, "mob-table-parent-4-child")}>
                    <div className={cn(flex15, boldCls)} cypress_id={"outfitCostRowTitleQ"}>
                      Outfit Cost
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"outfitCostQuantityQ"}>
                      {outfit?.quantity}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"outfitCostUnitRateQ"}>
                      {convertNumber({
                        number: outfit?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(flex1, centerCls)} cypress_id={"outfitCostTotalQ"}>
                      {convertNumber({
                        number: outfit?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                ) : null}
                {isReviewDiscountApplied ? (
                  <div className={cn(tableRowCls, "mob-table-parent-2-child")}>
                    <div className={cn(lastRowsCls, boldCls)}>
                      <span
                        cypress_id={"reviewDiscountTitleQ"}
                        className="margin-left text-[#009244] dark:text-[#4ADE80]"
                      >
                        {reviewDiscount.label}
                      </span>
                    </div>
                    <div
                      className={cn(flex1, centerCls, "text-[#009244] dark:text-[#4ADE80]")}
                      cypress_id={"reviewDiscountValueQ"}
                    >
                      {`\u2212${convertNumber({
                        number: reviewDiscount.amountOff,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}`}
                    </div>
                  </div>
                ) : null}
                <div className={cn(tableRowCls, "mob-table-parent-2-child")}>
                  <div className={cn(lastRowsCls, boldCls)}>
                    <span cypress_id={"subtotalTitleQ"} className="margin-left">
                      Subtotal
                    </span>
                  </div>
                  <div className={cn(flex1, centerCls)} cypress_id={"subtotalValueQ"}>
                    {convertNumber({
                      number: displaySubtotal,
                      currency: currencySign,
                      fixed: 2,
                      isUSClient: isUS,
                    })}
                  </div>
                </div>
                {getCouponCode()}
                {/* HUB-440: EDDM review-for-discount offer — same visibility
                    rule as the coupon (pre-submit, payment not skipped). The
                    row self-guards on the is_review_discount_enabled flag and
                    eligibility, so it renders nothing when unavailable. */}
                {showCoupon && reviewOfferComponent ? reviewOfferComponent() : null}
                {!taxNotCalculated && (
                  <div className={cn(tableRowCls, "mob-table-parent-error-child")}>
                    <div className="flex w-full">
                      <div className={cn(lastRowsCls, boldCls)}>
                        <span cypress_id={"taxTitleQ"} className="margin-left">
                          Tax
                        </span>
                      </div>
                      <div className={cn(flex1, centerCls)} cypress_id={"taxValueQ"}>
                        {convertNumber({
                          number: tax,
                          currency: currencySign,
                          fixed: 2,
                          isUSClient: isUS,
                        })}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-1 ml-auto mt-6 flex w-auto">
                  <div className="flex-[1.5] display-none" />
                  <div className="margin-bottom flex flex-[2] flex-col items-end bg-page px-[15px] py-3">
                    <div className="grand-total flex">
                      <div className={cn("rounded-l", boldCls)} cypress_id={"grandTotalTitleQ"}>
                        {taxNotCalculated ? "Total (before tax):" : "Grand Total:"}
                      </div>
                      <div
                        className={cn("w-[124px]", boldCls, centerCls)}
                        cypress_id={"grandTotalValueQ"}
                        data-copilot-readout="total"
                        style={{ textAlign: "right" }}
                      >
                        {convertNumberRounded({
                          number: displayTotalCost,
                          currency: currencySign,
                          fixed: 2,
                          isUSClient: isUS,
                        })}
                      </div>
                    </div>
                    {tax > 0 && (
                      <span className="pt-1 text-xs text-foreground">{isUK ? "*incl. VAT" : "*incl. taxes"}</span>
                    )}
                  </div>
                </div>
                {taxNotCalculated && (
                  <p className="mb-6 ml-auto flex text-sm font-normal italic leading-6 text-muted-foreground">
                    Taxes will be calculated during checkout
                  </p>
                )}
              </div>
            </div>
            <div className="justify-content mb-0 flex flex-col" cypress_id={"submitAndPayBtn"}>
              <SubmitCampaignFooter
                internalStatus={internalStatus}
                loading={loading}
                startDateError={startDate.error}
                billingBlocked={billingGate.blocked}
                flyerInfo={flyerInfo}
                isPaymentSkipped={isPaymentSkipped}
                handleClickSubmitAndPay={handleClickSubmitAndPay}
                isNotDesign={notExistingDesignFile}
                isBookingModalOpen={isBookingModalOpen}
                onBookReviewMeeting={handleOpenBookingModal}
                onCloseBookingModal={handleCloseBookingModal}
                onScheduleSuccess={onScheduleSuccess}
                submitter={{
                  firstName: submitter?.firstName || user?.firstName,
                  lastName: submitter?.lastName || user?.lastName,
                  email: submitter?.email || user?.email,
                }}
                country={country}
                campaignCost={totalCost}
                channel={channel}
                campaignId={campaignId}
                companyName={clientName}
                userId={user?.id}
              />
            </div>
          </Card>
        </div>
      </div>
      <div style={{ display: "none" }} className={classes.mainWrapper}>
        <div className={cn(classes.pagesWrapper, "mob-pages-wrapper")}>
          <div className={cn(classes.page, "mob-page")}>
            <div ref={firstPageRef}>
              <div className={classes.title}>Campaign Specifics</div>
              <div className={cn(classes.row, "mob-row")}>
                <div className={cn(classes.text)}>Campaign name</div>
                <div className={cn(classes.text)}>{campaignName}</div>
              </div>
              {displayOnCondition(
                !isDM,
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>City</div>
                  <div className={cn(classes.text)}>{cityName}</div>
                </div>
              )}
              <div className={cn(classes.row, "mob-row")}>
                <div className={cn(classes.text)}>Channel</div>
                <div className={cn(classes.text)}>{USER_CAMPAIGN_ENUM_TYPES[channel]}</div>
              </div>

              {isDM ? (
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>{"Recipients"}</div>
                  <div className={cn(classes.text)}>{flyersAmount}</div>
                </div>
              ) : (
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>{isD2D ? "Areas " : "Distributors "}</div>
                  <div className={cn(classes.text)}>{distribution?.quantity}</div>
                </div>
              )}

              <div className={cn(classes.flex, classes.cityRow)}>
                <div className={cn(classes.flex, classes.flex_1, "display-none")} />
                <div className={cn(classes.flex, classes.flex_1)}>
                  <div className={cn(classes.flex_1)}>
                    {internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ? (
                      <span onClick={toAudience} className={classes.link}>
                        {`Edit ${isDM ? "Segments" : "Audience"}`}
                      </span>
                    ) : null}
                  </div>
                  <div className={cn(classes.flex_1)} />
                </div>
              </div>
              {displayOnCondition(
                !isDM,
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>Flyers</div>
                  <div className={cn(classes.text)}>{printing?.quantity}</div>
                </div>
              )}
              {displayOnCondition(
                !isDM,
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>Campaign duration</div>
                  <div className={cn(classes.text)}>
                    {campaignDuration} week{campaignDuration !== 1 ? "s" : ""}
                  </div>
                </div>
              )}

              <div className={cn(classes.row, "mob-row")}>
                <div className={cn(classes.text)}>Campaign launch date</div>
                <div className={cn(classes.text)} data-copilot-readout="launchDate">
                  {customDateFormat(startDate.value, isUS)}
                </div>
              </div>

              <div className={cn(classes.row, "mob-row")}>
                <div className={cn(classes.text)}>Flyer type</div>
                <div className={cn(classes.text)}>{DocumentNames[flyerType]}</div>
              </div>
              {displayOnCondition(
                !isDM,
                <div className={cn(classes.row, "mob-row")}>
                  <div className={cn(classes.text)}>Flyer weight</div>
                  <div className={cn(classes.text)}>{flyerWeight}</div>
                </div>
              )}

              <div className={cn(classes.row, "mob-row")}>
                <div className={cn(classes.text)}>Flyer design</div>
                <div className={cn(classes.link)}>View design</div>
              </div>
              <div className={classes.line} />
              <div className={cn(classes.text)}>
                {isSubmitted ? "Submitted by:" : "Submitter:"}
                <span className={classes.marginL4}>{isSubmitted ? submittedBy : submitter}</span>
              </div>
            </div>
          </div>
          <div className={cn(classes.page, "mob-page")}>
            <div ref={secondPageRef}>
              <div className={classes.flex}>
                <div className={classes.title}>Quote</div>
                <div className={cn(classes.text, classes.quote)}>{quote}</div>
              </div>
              <div className={cn(classes.row, "mob-row", classes.detailsProvided)}>Please review details provided:</div>
              <div className={cn(classes.flex, classes.center)}>
                <div className={classes.campaignName}>{campaignName}</div>
                <div>{renderStatus(status, data.reviewMeetingDate)}</div>
              </div>
              <div className={cn(classes.row, "mob-row")}>
                <div>Company name</div>
                <div>{clientName}</div>
                <div />
              </div>
              <div className={cn(classes.row, "mob-row")}>
                <div>Date</div>
                <div>
                  {status === READABLE_CAMPAIGN_INTERNAL_STATUSES.DRAFT
                    ? formatDate(new Date(), "MMM d, yyyy")
                    : formatDate(+updatedAt, "MMM d, yyyy")}
                </div>
                <div />
              </div>
              <div className={cn(classes.table)}>
                <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                  <div className={cn(classes.flex_1_5, classes.bold)}>Description</div>
                  <div className={cn(classes.flex_1, classes.bold, classes.center)}>Quantity</div>
                  <div className={cn(classes.flex_1, classes.bold, classes.center)}>Unit rate</div>
                  <div className={cn(classes.flex_1, classes.bold, classes.center)}>Total</div>
                </div>

                {displayOnCondition(
                  isDM,
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Mailings</div>
                    <div className={cn(classes.flex_1, classes.center)}>{flyersAmount}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: costPerFlyer,
                        currency: currencySign,
                        fixed: 3,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: subtotal,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  distribution,
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Distribution Cost</div>
                    <div className={cn(classes.flex_1, classes.center)}>{distribution?.quantity}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: distribution?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: distribution?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  printing,
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Printing Cost</div>
                    <div className={cn(classes.flex_1, classes.center)}>{printing?.quantity}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {currencySign}
                      {toFixed3(printing?.rate || 0)}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: printing?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  travel,
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Travel Cost</div>
                    <div className={cn(classes.flex_1, classes.center)}>{travel?.quantity}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: travel?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: travel?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {hideOnNull(
                  permit,
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Permit Cost</div>
                    <div className={cn(classes.flex_1, classes.center)}>{permit?.quantity}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: permit?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: permit?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                )}
                {outfit?.total ? (
                  <div className={cn(classes.tableRow, "mob-table-parent-4-child")}>
                    <div className={cn(classes.flex_1_5, classes.bold)}>Outfit Cost</div>
                    <div className={cn(classes.flex_1, classes.center)}>{outfit?.quantity}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: outfit?.rate,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {convertNumber({
                        number: outfit?.total,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}
                    </div>
                  </div>
                ) : null}
                {isReviewDiscountApplied ? (
                  <div className={cn(classes.tableRow, "mob-table-parent-3-child")}>
                    <div className={cn(classes.flex_1_5)} />
                    <div className={cn(classes.flex_1, classes.bold)}>{reviewDiscount.label}</div>
                    <div className={cn(classes.flex_1, classes.center)}>
                      {`\u2212${convertNumber({
                        number: reviewDiscount.amountOff,
                        currency: currencySign,
                        fixed: 2,
                        isUSClient: isUS,
                      })}`}
                    </div>
                  </div>
                ) : null}
                <div className={cn(classes.tableRow, "mob-table-parent-3-child")}>
                  <div className={cn(classes.flex_1_5)} />
                  <div className={cn(classes.flex_1, classes.bold)}>Subtotal</div>
                  <div className={cn(classes.flex_1, classes.center)}>
                    {convertNumber({
                      number: displaySubtotal,
                      currency: currencySign,
                      fixed: 2,
                      isUSClient: isUS,
                    })}
                  </div>
                </div>
                {getCouponCode()}
                <div className={cn(classes.tableRow, "mob-table-parent-3-child")}>
                  <div className={cn(classes.flex_1_5)} />
                  <div className={cn(classes.flex_1, classes.bold)}>Tax</div>
                  <div className={cn(classes.flex_1, classes.center)}>
                    {convertNumber({
                      number: tax,
                      currency: currencySign,
                      fixed: 2,
                      isUSClient: isUS,
                    })}
                  </div>
                </div>
                <div className={cn(classes.row, classes.grandTotalWrapper)}>
                  <div className={cn(classes.flex_1)} />
                  <div className={cn(classes.grandTotalSubContainer_1, "margin-bottom")}>
                    <div className={classes.grandTotalSubContainer_2}>
                      <div className={cn(classes.grandTotal, classes.bold)}>
                        {taxNotCalculated ? "Total (before tax):" : "Grand Total:"}
                      </div>
                      <div
                        className={cn(classes.grandTotalCost, classes.bold, classes.center)}
                        data-copilot-readout="total"
                      >
                        {convertNumberRounded({
                          number: displayTotalCost,
                          currency: currencySign,
                          fixed: 2,
                          isUSClient: isUS,
                        })}
                      </div>
                    </div>
                    {tax > 0 && <span className={classes.taxText}>{isUK ? "*incl. VAT" : "*incl. taxes"}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(style)(QuoteView);
